import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { consultingServices } from '../../../../shared/consultingServices';
import usePageMeta from '../../hooks/usePageMeta';

const consultingServiceCopy = {
  'financial-operations': {
    label: 'Financial and Operational Improvement',
    description: 'Improve performance, efficiency, and financial stability across day-to-day operations.',
  },
  'strategic-growth': {
    label: 'Strategic Growth and Planning',
    description: 'Explore growth opportunities, planning priorities, and new service decisions.',
  },
  grants: {
    label: 'Grant Support',
    description: 'Get help finding, writing, or managing grant opportunities.',
  },
  workforce: {
    label: 'Workforce Retention and Team Stability',
    description: 'Strengthen retention, reduce burnout, and support workforce performance.',
  },
};

const consultingCategoryDisplayLabels = {
  'Benchmarked Operational and Financial Metric Analysis': 'Performance and benchmark analysis',
  'Market Analysis': 'Market analysis',
  'Strategic Planning': 'Strategic planning',
  'New Service Line Feasibility Studies': 'New service feasibility studies',
  'Grants Prospecting': 'Grant prospecting',
  'Proposal Development': 'Grant proposal development',
  'Grant Proposal Editing and Refinement': 'Grant proposal review and refinement',
  'Grant Management Services': 'Grant management support',
  'Grants Management Bundle': 'Full grant support package',
  'Staff Engagement & Satisfaction Assessments': 'Staff engagement assessments',
  'Burnout Risk Assessment': 'Burnout risk assessment',
  'Workforce Retention Strategy Development & Deployment': 'Retention strategy development',
  'Workforce Onboarding & Training: Customer Service & Resident Experience': 'Onboarding and resident experience training',
  'Workforce Consulting Bundle': 'Workforce consulting package',
};

const consultingServiceOptions = consultingServices.map((service) => ({
  value: service.id,
  label: consultingServiceCopy[service.id]?.label || service.label,
  description: consultingServiceCopy[service.id]?.description || service.description,
  categories: (service.subcategories || []).map((subcategory) => subcategory.label),
}));
const ltcOrganizationTypeOptions = [
  { value: 'nursing_home', label: 'Nursing Home' },
  { value: 'assisted_living', label: 'Assisted Living' },
  { value: 'long_term_care', label: 'Long-Term Care Group' },
  { value: 'behavioral_health', label: 'Behavioral Health' },
  { value: 'home_health_agency', label: 'Home Health Agency' },
  { value: 'other', label: 'Other LTC Organization' },
];

const serviceTypes = consultingServiceOptions.map(({ value, label, description }) => ({ value, label, description }));

const serviceCategories = consultingServiceOptions.reduce((acc, service) => {
  acc[service.value] = service.categories;
  return acc;
}, {});

const legacyServiceTypeMap = {
  'financial-operations': 'operations',
  'strategic-growth': 'strategic',
  grants: 'assessment',
  workforce: 'workforce',
};

const getLegacyServiceType = (serviceId) => legacyServiceTypeMap[serviceId];

const lmsAudienceOptions = ['RN', 'LPN', 'CNA', 'Med Tech', 'Dietary', 'Administrative', 'All Staff'];
const facilityTypeOptions = [
  { value: 'nursing_home', label: 'Nursing Home' },
  { value: 'assisted_living', label: 'Assisted Living' },
  { value: 'home_health', label: 'Home Health' },
  { value: 'rehab_center', label: 'Rehab Center' },
  { value: 'psychiatric', label: 'Psychiatric' },
  { value: 'hospice', label: 'Hospice' },
  { value: 'other', label: 'Other LTC Facility' },
];

const parseAudienceList = (value) => String(value || '')
  .split(',')
  .map((entry) => entry.trim())
  .filter(Boolean);

const buildAudienceValue = (selectedOptions, otherValue) => {
  const otherEntries = parseAudienceList(otherValue);
  return [...selectedOptions, ...otherEntries].join(', ');
};

const createBillingPartyState = () => ({
  type: 'organization',
  name: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  },
  apEmail: '',
  apPhone: '',
  notes: '',
});

const requiresBillingEntityName = (billingParty = {}) =>
  ['facility', 'custom'].includes(String(billingParty?.type || 'organization'));
const buildPublicCustomCourseRequest = (form) => {
  if (!form?.project?.serviceInterests?.lmsCustomDev) return undefined;
  const audience = parseAudienceList(form.project?.lmsScoping?.trainingAudience);
  const knownTopics = String(form.project?.lmsScoping?.knownCoursesOrTopics || '')
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  const goals = String(form.project?.lmsScoping?.trainingGoals || '')
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  const requiredTopics = String(form.project?.lmsScoping?.requiredTopics || '').trim();
  const courses = (knownTopics.length ? knownTopics : ['Custom course request']).map((title) => ({
    title,
    targetAudience: audience.filter((entry) => lmsAudienceOptions.includes(entry)),
    targetAudienceOther: audience.filter((entry) => !lmsAudienceOptions.includes(entry)).join(', '),
    learningObjectives: goals.slice(0, 5),
    notes: requiredTopics,
  }));
  return {
    requestType: 'new_course',
    packageName: form.project?.description || form.project?.goals || knownTopics[0] || 'Custom Course Package',
    currentSubscriptionType: form.project?.serviceInterests?.lmsHostingType === 'stratel' ? 'Host on Stratel LMS' : 'Deliver via SCORM / external platform',
    targetAudience: audience.filter((entry) => lmsAudienceOptions.includes(entry)),
    targetAudienceOther: audience.filter((entry) => !lmsAudienceOptions.includes(entry)).join(', '),
    estimatedLearners: form.project?.lmsScoping?.estimatedSeats || '',
    regulatoryDriver: requiredTopics,
    targetDeliveryWindow: form.timeline?.deadline || '',
    learningObjectives: goals.slice(0, 5),
    courses,
    additionalNotes: form.additionalInfo || '',
  };
};

// Common U.S. / American IANA time zones with friendly labels
const AMERICAN_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Detroit', label: 'Eastern (America/Detroit)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern (America/Indiana/Indianapolis)' },
  { value: 'America/Indiana/Knox', label: 'Central (America/Indiana/Knox)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Boise', label: 'Mountain (America/Boise)' },
  { value: 'America/Phoenix', label: 'Mountain (no DST) (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'America/Juneau', label: 'Alaska (America/Juneau)' },
  { value: 'America/Metlakatla', label: 'Alaska (America/Metlakatla)' },
  { value: 'America/Yakutat', label: 'Alaska (America/Yakutat)' },
  { value: 'America/Nome', label: 'Alaska (America/Nome)' },
  { value: 'America/Adak', label: 'Hawaii-Aleutian (America/Adak)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic (America/Puerto_Rico)' },
];

export default function IntakeFormPage() {
  usePageMeta({
    title: 'Get Started – Contact Stratel',
    description: 'Share a few details about your organization and the support you are looking for. Stratel will follow up with the right next steps.',
    keywords: 'contact Stratel, training inquiry, consulting inquiry, healthcare support request',
  });
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    // Contact
    contact: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      title: '',
      preferredContact: 'email',
    },
    // Organization
    organization: {
      name: '',
      type: '',
      website: '',
      size: '',
      city: '',
      state: '',
    },
    facilityMode: 'single',
    facilityEntryMode: 'list', // 'list' vs 'count' for multi-facility intakes
    facilityCount: 0,
    facilities: [{ name: '', type: '', city: '', state: '', notes: '' }],
    // Project
    project: {
      consultingEnabled: false,
      serviceType: '',
      serviceOffers: [{ type: '', categories: [] }],
      serviceInterests: {
        lms: false,
        lmsCustomPackage: false,
        staffing: false,
        lmsCustomDev: false,
        lmsHostingType: 'stratel',
      },
      lmsScoping: {
        trainingAudience: '',
        estimatedSeats: '',
        trainingGoals: '',
        requiredTopics: '',
        needRecommendation: true,
        knownCoursesOrTopics: '',
      },
      description: '',
      goals: '',
      challenges: '',
    },
    // Timeline
    timeline: {
      urgency: '',
      preferredStartDate: '',
      deadline: '',
    },
    // Meeting preferences
    meetingPreferences: {
      days: [],
      preferredTime: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    },
    // Budget
    budget: {
      hasBudget: null,
      range: '',
      approvalProcess: '',
    },
    billingParty: createBillingPartyState(),
    // Referral
    referralSource: '',
    referralDetails: '',
    additionalInfo: '',
  });

  const wantsStratelTraining = Boolean(form.project.serviceInterests.lms);
  const wantsCustomCatalogPackage = Boolean(form.project.serviceInterests.lmsCustomPackage);
  const wantsCustomTrainingDevelopment = Boolean(form.project.serviceInterests.lmsCustomDev);
  const selectedTrainingInterestCount = [
    wantsStratelTraining,
    wantsCustomCatalogPackage,
    wantsCustomTrainingDevelopment,
  ].filter(Boolean).length;
  const wantsAnyTrainingInterest = selectedTrainingInterestCount > 0;

  let trainingDetailsTitle = 'Training Details';
  let trainingDetailsDescription = 'You do not need to know the exact selection yet. Just tell us what kind of training support would be most helpful.';
  const stepLabels = {
    1: 'Contact and organization',
    2: 'Support interests',
    3: 'Timeline and budget',
    4: 'Final details',
  };

  if (selectedTrainingInterestCount > 1) {
    trainingDetailsTitle = 'Training Interests';
    trainingDetailsDescription = 'Tell us whether you want one of Stratel\'s standard offerings, a custom package built from Stratel\'s catalog, custom-built training for your organization, or a mix of those options.';
  } else if (wantsCustomCatalogPackage) {
    trainingDetailsTitle = 'Custom Catalog Package Details';
    trainingDetailsDescription = 'Tell us what courses or topics you want to mix into a package from Stratel\'s existing catalog. You do not need to know the exact course list yet.';
  } else if (wantsStratelTraining) {
    trainingDetailsTitle = 'Stratel Training Interests';
    trainingDetailsDescription = 'Tell us what kind of Stratel courses or packages you are interested in for your team. You do not need to know the exact selection yet.';
  } else if (wantsCustomTrainingDevelopment) {
    trainingDetailsTitle = 'Custom Training Development Details';
    trainingDetailsDescription = 'Tell us what kind of custom course or package you may want Stratel to build for your organization. You do not need to have everything defined yet.';
  }

  const updateForm = (section, field, value) => {
    if (section) {
      setForm(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const updateBillingParty = (patch) => {
    setForm((prev) => ({
      ...prev,
      billingParty: {
        ...prev.billingParty,
        ...patch,
      },
    }));
  };

  const updateServiceOffer = (index, patch) => {
    setForm((prev) => {
      const nextOffers = [...prev.project.serviceOffers];
      nextOffers[index] = { ...nextOffers[index], ...patch };
      return { ...prev, project: { ...prev.project, serviceOffers: nextOffers } };
    });
  };

  const addServiceOffer = () => {
    setForm((prev) => ({
      ...prev,
      project: {
        ...prev.project,
        serviceOffers: [...prev.project.serviceOffers, { type: '', categories: [] }],
      },
    }));
  };

  const removeServiceOffer = (index) => {
    setForm((prev) => ({
      ...prev,
      project: {
        ...prev.project,
        serviceOffers: prev.project.serviceOffers.filter((_, i) => i !== index),
      },
    }));
  };

  const toggleCategory = (offerIndex, category) => {
    setForm((prev) => {
      const nextOffers = [...prev.project.serviceOffers];
      const currentCategories = new Set(nextOffers[offerIndex].categories || []);
      if (currentCategories.has(category)) {
        currentCategories.delete(category);
      } else {
        currentCategories.add(category);
      }
      nextOffers[offerIndex] = {
        ...nextOffers[offerIndex],
        categories: Array.from(currentCategories),
      };
      return { ...prev, project: { ...prev.project, serviceOffers: nextOffers } };
    });
  };

  const toggleLmsAudience = (audience) => {
    setForm((prev) => {
      const current = parseAudienceList(prev.project.lmsScoping.trainingAudience);
      const knownSelections = current.filter((entry) => lmsAudienceOptions.includes(entry));
      const otherSelections = current.filter((entry) => !lmsAudienceOptions.includes(entry));
      const nextKnownSelections = knownSelections.includes(audience)
        ? knownSelections.filter((entry) => entry !== audience)
        : [...knownSelections, audience];
      return {
        ...prev,
        project: {
          ...prev.project,
          lmsScoping: {
            ...prev.project.lmsScoping,
            trainingAudience: buildAudienceValue(nextKnownSelections, otherSelections.join(', ')),
          },
        },
      };
    });
  };

  const updateFacility = (index, field, value) => {
    setForm((prev) => {
      const facilities = [...prev.facilities];
      const current = { ...(facilities[index] || { name: '', type: '', city: '', state: '', notes: '' }) };
      facilities[index] = {
        ...current,
        [field]: value,
      };
      return { ...prev, facilities };
    });
  };

  const setFacilityMode = (value) => {
    setForm((prev) => {
      const next = { ...prev, facilityMode: value };
      if (value === 'single') {
        next.facilityEntryMode = 'list';
        next.facilityCount = 0;
        if (!Array.isArray(prev.facilities) || !prev.facilities.length) {
          next.facilities = [{ name: '', type: '', city: '', state: '', notes: '' }];
        } else if (prev.facilities.length > 1) {
          next.facilities = [prev.facilities[0]];
        }
      }
      if (value === 'multiple') {
        next.facilityEntryMode = 'list';
      }
      return next;
    });
  };

  const setFacilityEntryMode = (value) => {
    setForm((prev) => ({
      ...prev,
      facilityEntryMode: value,
      // keep existing facility list, but clear if switching to count
      facilities: value === 'count' ? [] : prev.facilities.length ? prev.facilities : [{ name: '', type: '', city: '', state: '', notes: '' }],
    }));
  };

  const setFacilityCount = (value) => {
    setForm((prev) => ({
      ...prev,
      facilityCount: Number.isFinite(Number(value)) ? Number(value) : 0,
    }));
  };

  const addFacility = () => {
    setForm((prev) => ({
      ...prev,
      facilities: [...prev.facilities, { name: '', type: '', city: '', state: '', notes: '' }],
    }));
  };

  const removeFacility = (index) => {
    setForm((prev) => ({
      ...prev,
      facilities: prev.facilities.filter((_, facilityIndex) => facilityIndex !== index),
    }));
  };

  const setLmsAudienceOther = (value) => {
    setForm((prev) => {
      const current = parseAudienceList(prev.project.lmsScoping.trainingAudience);
      const knownSelections = current.filter((entry) => lmsAudienceOptions.includes(entry));
      return {
        ...prev,
        project: {
          ...prev.project,
          lmsScoping: {
            ...prev.project.lmsScoping,
            trainingAudience: buildAudienceValue(knownSelections, value),
          },
        },
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const consultingEnabled = Boolean(form.project.consultingEnabled);
      const hasSelectedService =
        consultingEnabled ||
        Boolean(form.project.serviceInterests?.lms) ||
        Boolean(form.project.serviceInterests?.lmsCustomPackage) ||
        Boolean(form.project.serviceInterests?.lmsCustomDev) ||
        Boolean(form.project.serviceInterests?.staffing);
      if (!hasSelectedService) {
        setError('Select at least one service before submitting your request.');
        return;
      }
      const validFacilities = (form.facilities || []).filter(
        (facility) => String(facility?.name || '').trim() || String(facility?.city || '').trim() || String(facility?.state || '').trim()
      );

      if (form.facilityMode === 'single' && !validFacilities.length) {
        setError('Add at least one facility so we can scope pricing correctly.');
        return;
      }

      if (form.facilityMode === 'multiple' && form.facilityEntryMode === 'count' && (!form.facilityCount || form.facilityCount < 1)) {
        setError('Provide the number of facilities or list them individually.');
        return;
      }

      const validOffers = consultingEnabled
        ? (form.project.serviceOffers || []).filter(
          (offer) => offer.type && Array.isArray(offer.categories) && offer.categories.length > 0
        )
        : [];
      const primaryOffer = validOffers[0];
      const resolvedServiceType = validOffers.length > 1
        ? 'multiple'
        : getLegacyServiceType(primaryOffer?.type);
      const payload = {
        contact: form.contact,
        organization: {
          name: form.organization.name,
          type: form.organization.type,
          website: form.organization.website,
          size: form.organization.size,
          address: {
            city: form.organization.city,
            state: form.organization.state,
          }
        },
        facilityMode: form.facilityMode,
        facilityCount: form.facilityMode === 'multiple' && form.facilityEntryMode === 'count'
          ? form.facilityCount
          : validFacilities.length,
        facilities: form.facilityMode === 'multiple' && form.facilityEntryMode === 'list'
          ? validFacilities.map((facility) => ({
            name: facility.name,
            type: facility.type,
            address: {
              city: facility.city,
              state: facility.state,
            },
            notes: facility.notes,
          }))
          : form.facilityMode === 'single'
            ? validFacilities.map((facility) => ({
              name: facility.name,
              type: facility.type,
              address: {
                city: facility.city,
                state: facility.state,
              },
              notes: facility.notes,
            }))
            : [],
        project: {
          serviceOffers: validOffers,
          serviceType: resolvedServiceType,
          serviceCategory: primaryOffer?.categories?.[0],
          serviceInterests: form.project.serviceInterests,
          lmsScoping: form.project.lmsScoping,
          description: form.project.description,
          goals: form.project.goals,
          challenges: form.project.challenges,
        },
        customCourseRequest: buildPublicCustomCourseRequest(form),
        timeline: {
          urgency: form.timeline.urgency,
          preferredStartDate: form.timeline.preferredStartDate || undefined,
          deadline: form.timeline.deadline || undefined,
        },
        meetingPreferences: {
          days: form.meetingPreferences.days || [],
          preferredTime: form.meetingPreferences.preferredTime || undefined,
          timezone: form.meetingPreferences.timezone || undefined,
        },
        budget: {
          hasBudget: form.budget.hasBudget === 'yes',
          range: form.budget.range || undefined,
          approvalProcess: form.budget.approvalProcess || undefined,
        },
        referralSource: form.referralSource,
        referralDetails: form.referralDetails,
        additionalInfo: form.additionalInfo,
      };

      const res = await fetch('/api/intake/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Failed to submit. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatMeetingPreferencesForDisplay = (mp) => {
    if (!mp) return '-';
    const days = (mp.days || []).map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ');
    let time = '';
    if (mp.preferredTime) {
      try {
        const t = new Date('1970-01-01T' + mp.preferredTime + ':00');
        time = t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      } catch (e) {
        time = mp.preferredTime;
      }
    }
    const parts = [days || 'Any day', time, mp.timezone].filter(Boolean);
    return parts.join(' • ');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You</h1>
          <p className="text-gray-600 mb-4">We have received your request and a Stratel team member will follow up within 1-2 business days.</p>

          <div className="bg-white border rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-2">When would you like us to reach out?</h3>
            <p className="text-sm text-gray-700">{formatMeetingPreferencesForDisplay(form.meetingPreferences)}</p>
          </div>

          <p className="text-sm text-gray-500 mb-6">If anything changes before we follow up, you can submit a new request with updated details.</p>

          <Link to="/" className="text-green-600 hover:text-green-700 font-medium">
            ← Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Tell Us How Stratel Can Help</h1>
            <p className="text-gray-600 mt-1">
              Share a few details about your organization and what you are looking for. We will follow up with the right next steps.
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {s}
                </div>
                {s < 4 && (
                  <div className={`w-16 sm:w-24 h-1 mx-2 ${step > s ? 'bg-green-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
            </div>
            <p className="text-sm text-gray-500">
              Step {step} of 4: {stepLabels[step]}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Contact & Organization */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={form.contact.firstName}
                      onChange={(e) => updateForm('contact', 'firstName', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={form.contact.lastName}
                      onChange={(e) => updateForm('contact', 'lastName', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.contact.email}
                    onChange={(e) => updateForm('contact', 'email', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={form.contact.phone}
                      onChange={(e) => updateForm('contact', 'phone', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={form.contact.title}
                      onChange={(e) => updateForm('contact', 'title', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="e.g., Director of Operations"
                    />
                  </div>
                </div>

                <hr className="my-6" />

                <h2 className="text-lg font-semibold text-gray-900">Organization Details</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                  <input
                    type="text"
                    required
                    value={form.organization.name}
                    onChange={(e) => updateForm('organization', 'name', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., ABC Health Systems"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization Type</label>
                    <select
                      value={form.organization.type}
                      onChange={(e) => updateForm('organization', 'type', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select type...</option>
                      {ltcOrganizationTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization Size</label>
                    <select
                      value={form.organization.size}
                      onChange={(e) => updateForm('organization', 'size', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select size...</option>
                      <option value="1-50">1-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={form.organization.city}
                      onChange={(e) => updateForm('organization', 'city', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={form.organization.state}
                      onChange={(e) => updateForm('organization', 'state', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="e.g., GA"
                    />
                  </div>
                </div>

                <hr className="my-6" />

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Organizational scope</h3>
                  <p className="text-sm text-gray-600">
                    Tell us whether this request is for a single location, multiple locations, or the whole organization.
                  </p>

                  <div className="space-y-3">
                    <label className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="facilityMode"
                        value="single"
                        checked={form.facilityMode === 'single'}
                        onChange={(e) => setFacilityMode(e.target.value)}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Single facility</span>
                        <span className="block text-sm text-gray-600">Use this when the request is for one facility only—either a truly single-site organization or one facility acting independently within a larger organization.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="facilityMode"
                        value="multiple"
                        checked={form.facilityMode === 'multiple'}
                        onChange={(e) => setFacilityMode(e.target.value)}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Multi-facility organization</span>
                        <span className="block text-sm text-gray-600">Use this when specific facilities are included and services should be tracked per facility in this request.</span>
                      </span>
                    </label>
                  </div>
                </div>

                {form.facilityMode === 'multiple' && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900">How would you like to provide facility info?</h4>
                    <div className="space-y-2">
                      <label className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="facilityEntryMode"
                          value="list"
                          checked={form.facilityEntryMode === 'list'}
                          onChange={(e) => setFacilityEntryMode(e.target.value)}
                          className="mt-1"
                        />
                        <span>
                          <span className="font-medium">List each facility</span>
                          <span className="block text-sm text-gray-600">Provide the address/name for each site.</span>
                        </span>
                      </label>
                      <label className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="facilityEntryMode"
                          value="count"
                          checked={form.facilityEntryMode === 'count'}
                          onChange={(e) => setFacilityEntryMode(e.target.value)}
                          className="mt-1"
                        />
                        <span>
                          <span className="font-medium">Provide a facility count only</span>
                          <span className="block text-sm text-gray-600">We’ll fill in details later when we follow up.</span>
                        </span>
                      </label>
                    </div>

                    {form.facilityEntryMode === 'count' && (
                      <div className="max-w-sm">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of facilities</label>
                        <input
                          type="number"
                          min={1}
                          value={form.facilityCount || ''}
                          onChange={(e) => setFacilityCount(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                    )}

                    {form.facilityEntryMode === 'list' && (
                      <>
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h3 className="text-base font-semibold text-gray-900">Facilities</h3>
                            <p className="text-sm text-gray-600">Add each facility in scope so pricing can be prepared per facility.</p>
                          </div>
                          <button
                            type="button"
                            onClick={addFacility}
                            disabled={form.facilityMode === 'single'}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          >
                            + Add Facility
                          </button>
                        </div>
                        <div className="space-y-4">
                          {form.facilities.map((facility, index) => (
                            <div key={`facility-${index}`} className="rounded-lg border border-gray-200 p-4 space-y-3">
                              <div className="flex items-center justify-between gap-3">
                                <h4 className="text-sm font-medium text-gray-900">Facility {index + 1}</h4>
                                {form.facilityMode === 'multiple' && form.facilities.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeFacility(index)}
                                    className="text-sm text-red-600 hover:text-red-700"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Facility Name</label>
                                  <input
                                    type="text"
                                    value={facility.name}
                                    onChange={(e) => updateFacility(index, 'name', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Facility Type</label>
                                  <select
                                    value={facility.type}
                                    onChange={(e) => updateFacility(index, 'type', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                  >
                                    <option value="">Select type...</option>
                                    {facilityTypeOptions.map((option) => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                  <input
                                    type="text"
                                    value={facility.city}
                                    onChange={(e) => updateFacility(index, 'city', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                  <input
                                    type="text"
                                    value={facility.state}
                                    onChange={(e) => updateFacility(index, 'state', e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                  rows={2}
                                  value={facility.notes}
                                  onChange={(e) => updateFacility(index, 'notes', e.target.value)}
                                  className="w-full border rounded-lg px-3 py-2"
                                  placeholder="Optional: beds, rollout notes, or anything specific to this facility"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

              </div>
            )}

            {/* Step 2: Support Interests */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-gray-900">What kind of support are you looking for?</h2>
                  <p className="text-sm text-gray-600">
                    Choose the areas you want to discuss. If you are still figuring it out, that is fine.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Areas of interest</label>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                    <label className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm max-w-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.project.consultingEnabled}
                        onChange={(e) => updateForm('project', 'consultingEnabled', e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block font-medium text-gray-900">Consulting Services</span>
                        <span className="block text-xs text-gray-500">Advisory support for operational, strategic, financial, or workforce priorities.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm max-w-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.project.serviceInterests.lms}
                        onChange={(e) => updateForm('project', 'serviceInterests', {
                          ...form.project.serviceInterests,
                          lms: e.target.checked,
                        })}
                        className="mt-1"
                      />
                      <span>
                        <span className="block font-medium text-gray-900">Stratel Training Courses and Packages</span>
                        <span className="block text-xs text-gray-500">Choose from Stratel's existing training offerings for your team.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm max-w-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.project.serviceInterests.lmsCustomPackage}
                        onChange={(e) => updateForm('project', 'serviceInterests', {
                          ...form.project.serviceInterests,
                          lmsCustomPackage: e.target.checked,
                        })}
                        className="mt-1"
                      />
                      <span>
                        <span className="block font-medium text-gray-900">Build a Custom Package From Stratel's Catalog</span>
                        <span className="block text-xs text-gray-500">Pick and choose from Stratel's existing courses to create a package that fits your team.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm max-w-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.project.serviceInterests.lmsCustomDev}
                        onChange={(e) => updateForm('project', 'serviceInterests', {
                          ...form.project.serviceInterests,
                          lmsCustomDev: e.target.checked,
                        })}
                        className="mt-1"
                      />
                      <span>
                        <span className="block font-medium text-gray-900">Custom Course and Package Development</span>
                        <span className="block text-xs text-gray-500">Have Stratel build custom training content for your organization.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm max-w-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.project.serviceInterests.staffing}
                        onChange={(e) => updateForm('project', 'serviceInterests', {
                          ...form.project.serviceInterests,
                          staffing: e.target.checked,
                        })}
                        className="mt-1"
                      />
                      <span>
                        <span className="block font-medium text-gray-900">Staffing Support</span>
                        <span className="block text-xs text-gray-500">Discuss staffing needs alongside consulting or training support.</span>
                      </span>
                    </label>
                  </div>
                  {!form.project.consultingEnabled &&
                    !form.project.serviceInterests.lms &&
                    !form.project.serviceInterests.lmsCustomPackage &&
                    !form.project.serviceInterests.lmsCustomDev &&
                    !form.project.serviceInterests.staffing && (
                      <p className="mt-2 text-sm text-amber-700">
                        Select at least one area so we know what to talk with you about.
                      </p>
                    )}
                </div>

                {form.project.serviceInterests.lmsCustomDev && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-600 font-medium">Where should the training live?</span>
                    <select
                      value={form.project.serviceInterests.lmsHostingType}
                      onChange={(e) => updateForm('project', 'serviceInterests', {
                        ...form.project.serviceInterests,
                        lmsHostingType: e.target.value,
                      })}
                      className="border border-gray-200 rounded px-2 py-1 bg-white"
                    >
                      <option value="stratel">Use Stratel's training platform</option>
                      <option value="external">Use your platform or SCORM delivery</option>
                    </select>
                  </div>
                )}

                {wantsAnyTrainingInterest && (
                  <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4 space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-purple-900">{trainingDetailsTitle}</h3>
                      <p className="mt-1 text-sm text-purple-800">
                        {trainingDetailsDescription}
                      </p>
                    </div>
                    {(() => {
                      const currentAudienceValues = parseAudienceList(form.project.lmsScoping.trainingAudience);
                      const selectedAudienceOptions = currentAudienceValues.filter((entry) => lmsAudienceOptions.includes(entry));
                      const otherAudienceValue = currentAudienceValues.filter((entry) => !lmsAudienceOptions.includes(entry)).join(', ');
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Who is this training for?</label>
                          <div className="flex flex-wrap gap-2">
                            {lmsAudienceOptions.map((audience) => (
                              <label key={audience} className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-gray-200 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedAudienceOptions.includes(audience)}
                                  onChange={() => toggleLmsAudience(audience)}
                                />
                                {audience}
                              </label>
                            ))}
                          </div>
                          <input
                            type="text"
                            value={otherAudienceValue}
                            onChange={(e) => setLmsAudienceOther(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 mt-3"
                            placeholder="Other team, department, or job title"
                          />
                        </div>
                      );
                    })()}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">About how many people need training?</label>
                        <input
                          type="text"
                          value={form.project.lmsScoping.estimatedSeats}
                          onChange={(e) => updateForm('project', 'lmsScoping', {
                            ...form.project.lmsScoping,
                            estimatedSeats: e.target.value,
                          })}
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="e.g., 25, 40-60, or not sure yet"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">What should this training help your team do better?</label>
                      <textarea
                        value={form.project.lmsScoping.trainingGoals}
                        onChange={(e) => updateForm('project', 'lmsScoping', {
                          ...form.project.lmsScoping,
                          trainingGoals: e.target.value,
                        })}
                        className="w-full border rounded-lg px-3 py-2"
                        rows={3}
                        placeholder="For example: strengthen onboarding, reinforce compliance, improve communication, or support a new initiative"
                      />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Topics you know should be covered</label>
                      <textarea
                        value={form.project.lmsScoping.requiredTopics}
                        onChange={(e) => updateForm('project', 'lmsScoping', {
                          ...form.project.lmsScoping,
                          requiredTopics: e.target.value,
                        })}
                        className="w-full border rounded-lg px-3 py-2"
                        rows={3}
                        placeholder={wantsCustomTrainingDevelopment && !wantsStratelTraining
                          ? 'For example: abuse prevention, onboarding, documentation, resident experience, or any topic you want built for your organization'
                          : 'For example: abuse prevention, HIPAA, dementia care, onboarding, documentation, or resident experience'}
                      />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={Boolean(form.project.lmsScoping.needRecommendation)}
                          onChange={(e) => updateForm('project', 'lmsScoping', {
                            ...form.project.lmsScoping,
                            needRecommendation: e.target.checked,
                          })}
                          className="mt-1"
                        />
                        <span>
                          <span className="block font-medium text-gray-900">I would like a recommendation from Stratel</span>
                          <span className="block text-xs text-gray-500">
                            {wantsStratelTraining && wantsCustomTrainingDevelopment
                              ? 'Choose this if you want help deciding between Stratel offerings, custom development, or a mix of both.'
                              : wantsCustomTrainingDevelopment
                                ? 'Choose this if you want help shaping the right custom course or package for your organization.'
                                : 'Choose this if you want help selecting the right Stratel courses or packages for your team.'}
                          </span>
                        </span>
                      </label>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Anything you already know</label>
                        <textarea
                          value={form.project.lmsScoping.knownCoursesOrTopics}
                          onChange={(e) => updateForm('project', 'lmsScoping', {
                            ...form.project.lmsScoping,
                            knownCoursesOrTopics: e.target.value,
                          })}
                          className="w-full border rounded-lg px-3 py-2"
                          rows={3}
                          placeholder={wantsCustomTrainingDevelopment && !wantsStratelTraining
                            ? 'Optional: course ideas, examples, internal requirements, source material, or anything else already on your mind'
                            : 'Optional: topics, course ideas, certifications, or anything else already on your mind'}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {form.project.consultingEnabled && (
                  <div>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Consulting support you want to discuss</label>
                      <p className="text-sm text-gray-600">
                        Choose the area that is closest to your need. We can narrow the scope with you later.
                      </p>
                    </div>
                    <div className="space-y-4">
                    {form.project.serviceOffers.map((offer, index) => (
                      <div key={`offer-${index}`} className="rounded-lg border border-gray-200 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-700">Consulting need {index + 1}</p>
                          {form.project.serviceOffers.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeServiceOffer(index)}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type of support</label>
                          <select
                            value={offer.type}
                            onChange={(e) => updateServiceOffer(index, { type: e.target.value, categories: [] })}
                            className="w-full border rounded-lg px-3 py-2"
                          >
                            <option value="">Select type of support...</option>
                            {serviceTypes.map((type) => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                          {offer.type && serviceTypes.find((type) => type.value === offer.type)?.description && (
                            <p className="mt-2 text-sm text-gray-600">
                              {serviceTypes.find((type) => type.value === offer.type)?.description}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Areas you want help with</label>
                          {offer.type ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {serviceCategories[offer.type]?.map((cat) => (
                                <label key={cat} className="flex items-start gap-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={(offer.categories || []).includes(cat)}
                                    onChange={() => toggleCategory(index, cat)}
                                  />
                                  <span>{consultingCategoryDisplayLabels[cat] || cat}</span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">Choose a type of support first.</p>
                          )}
                        </div>
                      </div>
                    ))}

                    <button type="button" onClick={addServiceOffer} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">
                      Add Another Consulting Need
                    </button>
                  </div>
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tell us a little more about what you need *
                  </label>
                  <textarea
                    required
                    value={form.project.description}
                    onChange={(e) => updateForm('project', 'description', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={4}
                    placeholder="Share a brief summary of what you are looking for, what prompted the request, or what kind of support would be most helpful."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What would a successful outcome look like?
                  </label>
                  <textarea
                    value={form.project.goals}
                    onChange={(e) => updateForm('project', 'goals', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={3}
                    placeholder="What would you like your team, organization, or program to be able to do better after this work?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What challenges are you trying to solve right now?
                  </label>
                  <textarea
                    value={form.project.challenges}
                    onChange={(e) => updateForm('project', 'challenges', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={3}
                    placeholder="Describe any current pain points, pressures, gaps, or priorities behind this request."
                  />
                </div>
              </div>
            )}

            {/* Step 3: Timeline & Budget */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">Timeline</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">How soon do you need to start? *</label>
                  <select
                    required
                    value={form.timeline.urgency}
                    onChange={(e) => updateForm('timeline', 'urgency', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select timeline...</option>
                    <option value="immediate">Immediately</option>
                    <option value="within_month">Within the next month</option>
                    <option value="within_quarter">Within the next quarter</option>
                    <option value="within_year">Within the next year</option>
                    <option value="exploring">Just exploring options</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Start Date</label>
                    <input
                      type="date"
                      value={form.timeline.preferredStartDate}
                      onChange={(e) => updateForm('timeline', 'preferredStartDate', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hard Deadline (if any)</label>
                    <input
                      type="date"
                      value={form.timeline.deadline}
                      onChange={(e) => updateForm('timeline', 'deadline', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                {/* Meeting Preferences */}
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">When would you like us to reach out?</h3>

                  <div className="mb-3 text-sm text-gray-700">
                    <div className="flex gap-3 flex-wrap">
                      {['sun','mon','tue','wed','thu','fri','sat'].map((d) => (
                        <label key={d} className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={(form.meetingPreferences.days || []).includes(d)}
                            onChange={(e) => {
                              const set = new Set(form.meetingPreferences.days || []);
                              if (e.target.checked) set.add(d); else set.delete(d);
                              updateForm('meetingPreferences', 'days', Array.from(set));
                            }}
                            className="w-4 h-4"
                          />
                          <span className="capitalize text-xs">{d}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time</label>
                      <input
                        type="time"
                        value={form.meetingPreferences.preferredTime}
                        onChange={(e) => updateForm('meetingPreferences', 'preferredTime', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                      <select
                        value={form.meetingPreferences.timezone}
                        onChange={(e) => updateForm('meetingPreferences', 'timezone', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        {AMERICAN_TIMEZONES.map((tz) => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                        {form.meetingPreferences.timezone && !AMERICAN_TIMEZONES.some(t => t.value === form.meetingPreferences.timezone) && (
                          <option value={form.meetingPreferences.timezone}>{form.meetingPreferences.timezone}</option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                <hr className="my-6" />

                <h2 className="text-lg font-semibold text-gray-900">Budget</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Do you have a budget approved for this engagement? *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="hasBudget"
                        value="yes"
                        checked={form.budget.hasBudget === 'yes'}
                        onChange={(e) => updateForm('budget', 'hasBudget', e.target.value)}
                        className="w-4 h-4 text-green-600"
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="hasBudget"
                        value="no"
                        checked={form.budget.hasBudget === 'no'}
                        onChange={(e) => updateForm('budget', 'hasBudget', e.target.value)}
                        className="w-4 h-4 text-green-600"
                      />
                      No / Not Yet
                    </label>
                  </div>
                </div>

                {form.budget.hasBudget === 'yes' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      What is your budget range?
                    </label>
                    <select
                      value={form.budget.range}
                      onChange={(e) => updateForm('budget', 'range', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select range...</option>
                      <option value="under_10k">Under $10,000</option>
                      <option value="10k_25k">$10,000 - $25,000</option>
                      <option value="25k_50k">$25,000 - $50,000</option>
                      <option value="50k_100k">$50,000 - $100,000</option>
                      <option value="100k_250k">$100,000 - $250,000</option>
                      <option value="250k_plus">$250,000+</option>
                      <option value="unsure">Unsure</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Who needs to approve the engagement?
                  </label>
                  <input
                    type="text"
                    value={form.budget.approvalProcess}
                    onChange={(e) => updateForm('budget', 'approvalProcess', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., Board approval required, CEO sign-off"
                  />
                </div>

                <hr className="my-6" />
              </div>
            )}

            {/* Step 4: Final Details */}
            {step === 4 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">Final Details</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">How did you hear about us?</label>
                  <select
                    value={form.referralSource}
                    onChange={(e) => updateForm(null, 'referralSource', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select...</option>
                    <option value="web_search">Web Search</option>
                    <option value="referral">Referral</option>
                    <option value="conference">Conference / Event</option>
                    <option value="social_media">Social Media</option>
                    <option value="previous_client">Previous Client</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {(form.referralSource === 'referral' || form.referralSource === 'conference') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {form.referralSource === 'referral' ? 'Who referred you?' : 'Which event?'}
                    </label>
                    <input
                      type="text"
                      value={form.referralDetails}
                      onChange={(e) => updateForm(null, 'referralDetails', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Anything else you'd like us to know?
                  </label>
                  <textarea
                    value={form.additionalInfo}
                    onChange={(e) => updateForm(null, 'additionalInfo', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={4}
                  />
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">What happens next?</h3>
                  <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                    <li>We'll review your submission within 1-2 business days</li>
                    <li>A team member will reach out to schedule a discovery call</li>
                    <li>We'll prepare a customized proposal based on your needs</li>
                    <li>Once approved, we'll send a Master Service Agreement (MSA)</li>
                  </ol>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Previous
                </button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (step === 2) {
                      const hasSelectedService =
                        Boolean(form.project.consultingEnabled) ||
                        Boolean(form.project.serviceInterests?.lms) ||
                        Boolean(form.project.serviceInterests?.lmsCustomPackage) ||
                        Boolean(form.project.serviceInterests?.lmsCustomDev) ||
                        Boolean(form.project.serviceInterests?.staffing);
                      if (!hasSelectedService) {
                        setError('Select at least one area of interest before continuing.');
                        return;
                      }
                    }
                    setError('');
                    setStep(step + 1);
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting || form.budget.hasBudget === null}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
